/**
 * ChatHistoryModal — stored Deal-Analyst conversations for the project, newest
 * first. Click a row to reopen & continue it (its scenarios reactivate too).
 * Deleting a chat also removes the scenarios it owns, unless another chat still
 * references them (the confirmation names what will go).
 */
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { requestOpenChat, deleteChatAndScenarios } from "Redux/chats/actions";

dayjs.extend(relativeTime);

const ChatHistoryModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const chats = useSelector((s) => s.chats.list);

  const open = (id) => {
    dispatch(requestOpenChat(id));
    onClose();
  };

  const del = async (chat, e) => {
    e.stopPropagation();
    const others = chats.filter((c) => c.chatId !== chat.chatId);
    const refElsewhere = new Set(others.flatMap((c) => (c.scenarios || []).map((s) => s.id)));
    const willDelete = (chat.scenarios || [])
      .filter((s) => !refElsewhere.has(s.id))
      .map((s) => s.name || s.id);
    const msg = willDelete.length
      ? `Delete "${chat.title}" and its scenario(s): ${willDelete.join(", ")}?`
      : `Delete "${chat.title || "this chat"}"?`;
    if (window.confirm(msg)) await dispatch(deleteChatAndScenarios(chat.chatId));
  };

  return (
    <div className="modal is-active">
      <div className="modal-background" style={{ backgroundColor: "rgba(0,0,0,0.6)" }} onClick={onClose} />
      <div className="modal-card" style={{ maxWidth: "560px", borderRadius: "var(--ev-radius-lg)", overflow: "hidden" }}>
        <header style={{ background: "linear-gradient(135deg, #430098 0%, #79589F 100%)", padding: "1rem 1.25rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <i className="fa-duotone fa-comments" style={{ color: "#fff" }} />
          <span style={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem", flex: 1 }}>Saved chats</span>
          <button className="delete" onClick={onClose} />
        </header>
        <section style={{ padding: "0.75rem", backgroundColor: "var(--ev-white)", maxHeight: "60vh", overflowY: "auto" }}>
          {chats.length === 0 && (
            <div style={{ textAlign: "center", color: "var(--ev-text-muted)", padding: "2rem 1rem", fontSize: "0.85rem" }}>
              No saved chats yet.
            </div>
          )}
          {chats.map((chat) => (
            <div
              key={chat.chatId}
              onClick={() => open(chat.chatId)}
              className="ev-chat-row"
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "0.6rem",
                padding: "0.6rem 0.7rem",
                borderRadius: "8px",
                border: "1px solid var(--ev-border)",
                marginBottom: "0.4rem",
                cursor: "pointer",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "var(--ev-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {chat.title || "Deal analysis chat"}
                  </span>
                  {chat.scenarios?.length > 0 && (
                    <span className="tag is-small" style={{ backgroundColor: "var(--ev-purple-light)", color: "var(--ev-purple-dark)", borderRadius: "4px", fontSize: "0.62rem", fontWeight: 700 }}>
                      <i className="fa-duotone fa-flask" style={{ marginRight: "0.25rem" }} />
                      {chat.scenarios.length}
                    </span>
                  )}
                </div>
                {chat.preview && (
                  <div style={{ fontSize: "0.74rem", color: "var(--ev-text-muted)", marginTop: "0.15rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {chat.preview}
                  </div>
                )}
                <div style={{ fontSize: "0.66rem", color: "var(--ev-text-muted)", marginTop: "0.2rem" }}>
                  {chat.updatedAt ? dayjs(chat.updatedAt).fromNow() : ""}
                </div>
              </div>
              <button
                className="button is-small is-light"
                title="Delete chat"
                onClick={(e) => del(chat, e)}
                style={{ borderRadius: "6px", color: "var(--ev-danger)", flexShrink: 0 }}
              >
                <i className="fa-solid fa-trash" />
              </button>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
};

export default ChatHistoryModal;
